import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ChevronsUpDown, Loader2, LogOut, Mail, Plus, X } from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getAllOrganizations, getCurrentOrganization, getOrganizationId } from '@/data/store/selectors';
import { storeOrganizationId } from '@/data/store/actions';
import { useOrgInvitations } from '@/hooks/useOrgInvitations';
import { orgInitials } from '@/lib/components/common/navbar/orgInitials';
import { cn } from '@/lib/utils';

/**
 * Organization/project avatar: the uploaded logo when there is one, initials
 * otherwise. `className` carries the size and the fallback's colours, which
 * differ between the trigger, the invitation rows and the list.
 */
function OrgAvatar({ name, imageUrl, className }: { name: string; imageUrl?: string | null; className: string }) {
  return (
    <span className={cn('flex shrink-0 items-center justify-center overflow-hidden font-bold', className)}>
      {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-cover" /> : orgInitials(name)}
    </span>
  );
}

interface OrgSwitcherProps {
  /** Close the navbar dropdown after navigating away. */
  onNavigate?: () => void;
}

/**
 * Organization/project switcher that heads the navbar menu. Shows the active
 * one, and opens a menu to switch to another, leave for the selection screen, or
 * create a new one.
 *
 * It also surfaces pending invitations to *other* organizations/projects: listed
 * at the top of the open menu with accept/decline, and reduced to an unread-style
 * count badge while the menu is closed — otherwise a user who already has an
 * active organization would never see an invite, since the invitation cards only
 * render on the `/organizations` screen they no longer pass through.
 */
export default function OrgSwitcher({ onNavigate }: OrgSwitcherProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const organizations = useSelector(getAllOrganizations);
  const current = useSelector(getCurrentOrganization);
  const activeId = useSelector(getOrganizationId);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { invitations, count, pendingOrgId, acceptInvitation, declineInvitation } = useOrgInvitations();

  const activeOrg = current ?? organizations.find((o) => o.id === activeId) ?? null;
  const activeName = activeOrg?.name ?? 'Χωρίς οργανισμό';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const switchTo = (id: string) => {
    if (id !== activeId) {
      dispatch(storeOrganizationId(id));
      navigate('/dashboard');
      onNavigate?.();
    }
    setOpen(false);
  };

  /**
   * Leave the active organization/project. Clearing the id first matters:
   * `useNavigation` bounces anyone who still has one back to the dashboard the
   * moment they land on `/organizations`.
   */
  const exitOrganization = () => {
    setOpen(false);
    dispatch(storeOrganizationId(null));
    navigate('/organizations');
    onNavigate?.();
  };

  const goToCreate = () => {
    setOpen(false);
    navigate('/organizations/create');
    onNavigate?.();
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left transition-all hover:border-gray-300 hover:bg-gray-50"
      >
        <span className="relative shrink-0">
          <OrgAvatar
            name={activeName}
            imageUrl={activeOrg?.image_url}
            className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-sm text-white shadow-lg"
          />
          {/* Count only while collapsed — once the menu is open the invitations
              are right there, so the badge would be repeating itself. */}
          <AnimatePresence>
            {!open && count > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                aria-label={`${count} προσκλήσεις`}
                className="absolute -top-1.5 -right-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] leading-none font-bold text-white ring-2 ring-white"
              >
                {count > 9 ? '9+' : count}
              </motion.span>
            )}
          </AnimatePresence>
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
            Οργανισμός / Έργο
          </span>
          <span className="block truncate text-base font-semibold text-gray-900">{activeName}</span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 left-0 z-50 mt-2 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl"
          >
            {count > 0 && (
              <>
                <div className="flex items-center gap-1.5 px-2.5 pt-1 pb-2 text-[10px] font-semibold tracking-wider text-amber-600 uppercase">
                  <Mail className="h-3 w-3 shrink-0" />
                  Προσκλήσεις
                </div>
                <div className="space-y-1.5">
                  {invitations.map((inv) => {
                    const busy = pendingOrgId === inv.id;
                    return (
                      <div
                        key={inv.id}
                        className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-2.5 py-2"
                      >
                        <OrgAvatar
                          name={inv.name}
                          imageUrl={inv.image_url}
                          className="h-8 w-8 rounded-lg bg-amber-500 text-[11px] text-white"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm leading-tight font-semibold text-gray-900">{inv.name}</p>
                          <p className="truncate text-[11px] text-gray-500">σας προσκάλεσε</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => declineInvitation(inv)}
                            disabled={busy}
                            title="Απόρριψη"
                            className="cursor-pointer rounded-full p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => acceptInvitation(inv)}
                            disabled={busy}
                            title="Αποδοχή"
                            className="cursor-pointer rounded-full bg-blue-600 p-1.5 text-white transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-60"
                          >
                            {busy ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="my-1.5 h-px bg-gray-200" />
              </>
            )}

            {organizations.length > 0 && (
              <div className="max-h-64 overflow-y-auto">
                {organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => switchTo(org.id)}
                    className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-gray-50"
                  >
                    <OrgAvatar
                      name={org.name}
                      imageUrl={org.image_url}
                      className="h-8 w-8 rounded-lg bg-gray-100 text-[11px] text-gray-600"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{org.name}</span>
                    {org.id === activeId && <Check className="h-4 w-4 shrink-0 text-emerald-500" />}
                  </button>
                ))}
              </div>
            )}

            <div className="my-1.5 h-px bg-gray-200" />
            <button
              onClick={goToCreate}
              className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-gray-50"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-300 text-gray-400">
                <Plus className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium text-gray-900">Νέος οργανισμός / έργο</span>
            </button>
            <button
              onClick={exitOrganization}
              className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-red-50"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 transition-colors group-hover:bg-red-100 group-hover:text-red-600">
                <LogOut className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium text-gray-700 transition-colors group-hover:text-red-600">
                Έξοδος από τον οργανισμό
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
